import type { SxProps, Theme } from '@mui/material';

/* The dark public-page treatment, lifted from the TCG and physical-cards pages.
 *
 * These values are NOT invented for this funnel — they are the ones
 * `features/TradingCardGame/TradingCardGame.tsx` and
 * `features/PhysicalCards/PublicPhysicalCards.tsx` already use, pulled into one
 * place so the three pages cannot drift. The giveaway funnel (`RafflePage`) is
 * the odd one out: it forces `document.body` white and styles for a light page.
 * This funnel deliberately follows the TCG look instead.
 *
 * If the public palette ever changes, this file and those two pages move
 * together — there is no shared token module for public marketing pages
 * (`components/DesignSystem/designTokens.ts` is the member-portal/design-system
 * one, and `landing.css` carries only `.inc-orbitron` and two animations).
 */

export const PAGE_BG = '#070c13';
export const CYAN = '#38e8ff';
export const GOLD = '#ffcf40';

export const INK = '#e8f4fb';
export const BODY = '#c7d6e0';
export const MUTED = '#8ea3b3';
export const SOFT = '#a8bccb';
export const LINE = 'rgba(140,170,190,0.16)';

export const MONO = 'ui-monospace, Menlo, Consolas, monospace';

/** The cyan eyebrow above every section heading. */
export const kickerSx: SxProps<Theme> = {
  font: `800 11px ${MONO}`,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: CYAN
};

/** The gold primary action, ink text — same as the TCG page's. */
export const ctaSx: SxProps<Theme> = {
  px: 4,
  py: 1.35,
  borderRadius: 999,
  fontWeight: 900,
  fontSize: 16,
  textTransform: 'none',
  color: '#04121a',
  background: `linear-gradient(180deg,#ffdd6e,${GOLD})`,
  boxShadow: '0 14px 34px -12px rgba(255,207,64,0.7)',
  '&:hover': { background: 'linear-gradient(180deg,#ffe790,#ffd85c)' },
  '&.Mui-disabled': {
    background: 'rgba(140,170,190,0.18)',
    color: 'rgba(232,244,251,0.45)',
    boxShadow: 'none'
  }
};

/** A raised surface on the dark ground. */
export const panelSx: SxProps<Theme> = {
  background: 'linear-gradient(180deg, rgba(15,22,32,0.9), rgba(9,14,21,0.9))',
  border: `1px solid ${LINE}`,
  borderRadius: '18px'
};

/* Text inputs on a dark ground.
 *
 * Written out rather than reusing `portalUi`'s field helper: that one is built
 * for the member portal's dialogs and carries its own sizing and spacing
 * assumptions. The selects still use `portalSelect`'s `selectSx`/`selectMenuProps`,
 * which `PublicPhysicalCards` already proves render correctly on this background. */
export const darkFieldSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    background: 'rgba(255,255,255,0.04)',
    borderRadius: '10px',
    color: INK,
    '& fieldset': { borderColor: LINE },
    '&:hover fieldset': { borderColor: 'rgba(56,232,255,0.45)' },
    '&.Mui-focused fieldset': { borderColor: CYAN }
  },
  /* 16px on phones, never less. iOS Safari zooms the page in when a focused
     input's text is smaller than 16px, and the zoom OUTLIVES the signup: this is
     a single-page app, so the confirmation page arrives in the same, still-zoomed
     document, cut off on the right and scrolling sideways. That was the "overflow"
     reported from the page after sign-up. The desktop size stays as it was. */
  '& .MuiInputBase-input': { fontSize: { xs: 16, sm: 14.5 }, color: INK },
  '& .MuiInputBase-input::placeholder': { color: '#5c6b78', opacity: 1 },
  '& .MuiFormHelperText-root': { color: '#ff8b8b', marginLeft: 0 },
  '& .MuiInputAdornment-root .MuiTypography-root': { color: MUTED }
};

/** The small caps label above each field. */
export const fieldLabelSx: SxProps<Theme> = {
  fontSize: 12.5,
  fontWeight: 800,
  letterSpacing: '0.08em',
  color: MUTED,
  mb: 0.75
};
